/**
 * Main card form container component
 */


import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CardData, CardType, OrderContext, BillingAddress } from '../types/CardData';
import { SubmitButtonConfig } from '../types/PostMessageEvent';
import { validateCardData, getBrandExclusionError, cleanValidationErrors, ALL_SUPPORTED_BRANDS } from '../services/validationService';
import { detectCardType } from '../services/cardTypeDetector';
import { apiService } from '../services/apiService';
import { postMessageService } from '../services/postMessageService';
import { CardNumberInput } from './CardNumberInput';
import { CVVInput } from './CVVInput';
import { BillingAddressForm } from './BillingAddressForm';
import { ExpiryDateInput } from './ExpiryDateInput';

import {
  isValidCSSColor,
  darkenColor,
  DEFAULT_SUBMIT_BUTTON_COLOR,
  DEFAULT_SUBMIT_BUTTON_FOREGROUND_COLOR,
  DEFAULT_SUBMIT_BUTTON_LABEL
} from '../utils/colorUtils';
import "./CardForm.css";

// Maps backend expiry validation messages to user-friendly frontend equivalents.
// Comparison is trim + case-insensitive to handle minor backend wording changes.
// Only these cases show specific messages; all others use the generic fallback.
const EXPIRY_MESSAGE_MAP: Array<{ match: string; display: string }> = [
  // Expired card (past year)
  {
    match: 'year should be greater than current year.',
    display: 'Card has expired, please enter a valid expiration date',
  },
  // Expired card (past month, same year)
  {
    match: 'month should be greater than current month.',
    display: 'Card has expired, please enter a valid expiration date',
  },
  // Too far in the future
  {
    match: 'year should not be greater than 10 years from current year.',
    display: 'Card expiration date cannot be more than 10 years from today',
  },
];

function mapExpiryMessage(backendMsg: string): string {
  const normalized = backendMsg.trim().toLowerCase();
  const found = EXPIRY_MESSAGE_MAP.find(e => e.match === normalized);
  return found ? found.display : 'Expiration date must be in MM/YY format';
}

export interface CardFormContainerProps {
  orderContext: OrderContext;
  bearerToken: string | null;
  apiUrl: string | null;
  correlationId?: string | null;
  submitButtonConfig?: SubmitButtonConfig;
}

interface SuccessData {
  cardType: string;
  last4Digits: string;
  verificationDate: string;
}

const formatTimestampToDate = (isoTimestamp: string): string => {
  const d = new Date(isoTimestamp);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const formatCardTypeLabel = (cardType: string): string => {
  const labels: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
  };
  return labels[cardType.toLowerCase()] ?? cardType;
};

export const CardFormContainer: React.FC<CardFormContainerProps> = ({
                                                                      orderContext,
                                                                      bearerToken,
                                                                      apiUrl,
                                                                      correlationId,
                                                                      submitButtonConfig: propSubmitButtonConfig,
                                                                    }) => {
  const runtimeApiUrl =
      (window as { CARDVAULT_CONFIG?: { apiUrl?: string; apiBaseUrl?: string } }).CARDVAULT_CONFIG?.apiUrl ||
      (window as { CARDVAULT_CONFIG?: { apiUrl?: string; apiBaseUrl?: string } }).CARDVAULT_CONFIG?.apiBaseUrl ||
      null;
  const resolvedApiUrl = apiUrl || runtimeApiUrl || import.meta.env.VITE_API_URL || null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Submit button configuration with defaults
  const [submitButtonConfig, setSubmitButtonConfig] = useState<Required<SubmitButtonConfig>>({
    show: propSubmitButtonConfig?.show !== undefined ? propSubmitButtonConfig.show : true,
    label: propSubmitButtonConfig?.label || DEFAULT_SUBMIT_BUTTON_LABEL,
    color: propSubmitButtonConfig?.color || DEFAULT_SUBMIT_BUTTON_COLOR,
    foregroundColor: propSubmitButtonConfig?.foregroundColor || DEFAULT_SUBMIT_BUTTON_FOREGROUND_COLOR,
    disabled: propSubmitButtonConfig?.disabled || false,
  });

  const [cardData, setCardData] = useState<Partial<CardData>>({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: '',
    billingAddress: {
      address1: '',
      address2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
    },
  });

  const [cardType, setCardType] = useState<CardType>('unknown');
  const [brandExclusionError, setBrandExclusionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);
  const [isButton, setIsButton] = useState<boolean | null>(false);

  // Use ref to store latest submit handler so event listener always calls current version
  const handleSubmitRef = React.useRef<(() => Promise<void>) | null>(null);
  // Prevents the real-time validation useEffect from overwriting field errors that were
  // just set from a backend 400 response. Cleared on the next cardData change.
  const apiFieldErrorsRef = React.useRef(false);

  // Update submit button config when prop changes
  useEffect(() => {
    console.log('[CardFormContainer] useEffect triggered');
   
    if (propSubmitButtonConfig) {
      console.log('[CardFormContainer] Processing submit button config');
      console.log('[CardFormContainer] prop.show value:', propSubmitButtonConfig.show);
      console.log('[CardFormContainer] prop.show !== undefined:', propSubmitButtonConfig.show !== undefined);

      const newConfig = {
        show: propSubmitButtonConfig.show !== undefined ? propSubmitButtonConfig.show : true,
        label: propSubmitButtonConfig.label || DEFAULT_SUBMIT_BUTTON_LABEL,
        color: propSubmitButtonConfig.color || DEFAULT_SUBMIT_BUTTON_COLOR,
        foregroundColor: propSubmitButtonConfig.foregroundColor || DEFAULT_SUBMIT_BUTTON_FOREGROUND_COLOR,
        disabled: propSubmitButtonConfig.disabled || false,
      };

      console.log('[CardFormContainer] newConfig.show:', newConfig.show);

      // Validate background color
      if (!isValidCSSColor(newConfig.color)) {
        console.warn('[CardFormContainer] Invalid button color:', newConfig.color, '- using default');
        newConfig.color = DEFAULT_SUBMIT_BUTTON_COLOR;
      }

      // Validate foreground color
      if (!isValidCSSColor(newConfig.foregroundColor)) {
        console.warn('[CardFormContainer] Invalid button foreground color:', newConfig.foregroundColor, '- using default');
        newConfig.foregroundColor = DEFAULT_SUBMIT_BUTTON_FOREGROUND_COLOR;
      }

      // Validate label
      if (newConfig.label.trim() === '') {
        console.warn('[CardFormContainer] Empty button label - using default');
        newConfig.label = DEFAULT_SUBMIT_BUTTON_LABEL;
      } else if (newConfig.label.length > 50) {
        console.warn('[CardFormContainer] Button label too long - truncating');
        newConfig.label = newConfig.label.substring(0, 50);
      }

      console.log('[CardFormContainer] Setting submitButtonConfig to:', JSON.stringify(newConfig, null, 2));
      setSubmitButtonConfig(newConfig);
    } else {
      console.log('[CardFormContainer] propSubmitButtonConfig is falsy, not updating');
    }
  }, [propSubmitButtonConfig]);

  // NOTE: Validation only happens when Submit button is clicked
  // No real-time validation state updates to parent

  // Initialize API service with bearer token and API URL (Phase 3)
  useEffect(() => {
    if (resolvedApiUrl) {
      console.log('[CardFormContainer] Setting API URL:', resolvedApiUrl);
      apiService.setApiUrl(resolvedApiUrl);
    }

    if (bearerToken) {
      console.log('[CardFormContainer] Setting bearer token for API authentication');
      apiService.setBearerToken(bearerToken);
    }

    // Apply caller-supplied correlation ID, or clear it to revert to auto-generation
    apiService.setCorrelationId(correlationId ?? null);
  }, [bearerToken, resolvedApiUrl, correlationId]);

  // Listen for SUBMIT_CARD_FORM message from parent window
  useEffect(() => {
    const handleSubmitTrigger = () => {
      if (handleSubmitRef.current) {
        handleSubmitRef.current();
      }
    };

    postMessageService.on('SUBMIT_CARD_FORM', handleSubmitTrigger);

    // Signal SDK that the widget is fully ready to accept submits.
    // Sent here (not in App.tsx) so the SUBMIT_CARD_FORM listener above
    // is guaranteed to exist before the SDK can replay a queued submit.
    postMessageService.send('WIDGET_INITIALIZED', { version: '1.0.0' });

    return () => {

      postMessageService.off('SUBMIT_CARD_FORM', handleSubmitTrigger);
    };
  }, []); // Empty deps - register only once, but uses ref to get latest handler

  // Send resize messages when content changes
  useEffect(() => {
    const height = document.body.scrollHeight;
    postMessageService.sendResize(height);
  }, [cardData, error, isSubmitting]);

  useEffect(() => {
    // Only validate if form has been submitted at least once
    if (hasAttemptedSubmit) {
      // Skip this run if field errors were just written from a backend 400 response.
      // The next cardData change (user editing a field) will clear this flag and
      // resume normal real-time local validation.
      if (apiFieldErrorsRef.current) {
        apiFieldErrorsRef.current = false;
        return;
      }

      const validation = validateCardData(cardData);

      const currentCardType = detectCardType(cardData.cardNumber || '');
      const brandError = getBrandExclusionError(currentCardType, orderContext.excludeCards || []);

      const mergedErrors = { ...validation.errors };
      if (brandError) {
        mergedErrors.cardNumber = brandError;
        setBrandExclusionError(brandError);
      }
      if (!validation.isValid || brandError) {
        setFieldErrors(mergedErrors);
      } else {
        // Clear all errors if validation passes
        setFieldErrors({});
        setBrandExclusionError(null);
      }
    }
  }, [cardData, hasAttemptedSubmit, orderContext.excludeCards]);

  const handleCardNumberChange = (value: string, type: CardType) => {
    setCardData((prev) => ({ ...prev, cardNumber: value }));
    setCardType(type);
    setBrandExclusionError(null);
    if (fieldErrors.cardNumber) {
      setFieldErrors((prev) => ({ ...prev, cardNumber: '' }));
    }
  };


  const handleExpiryDateChange = (month: string, year: string) => {
    setCardData((prev) => ({ ...prev, expiryMonth: month, expiryYear: year }));
    if (fieldErrors.expiryDate) {
      setFieldErrors((prev) => ({ ...prev, expiryDate: '' }));
    }
  };

  const handleCvvChange = (value: string) => {
    setCardData((prev) => ({ ...prev, cvv: value }));
    if (fieldErrors.cvv) {
      setFieldErrors((prev) => ({ ...prev, cvv: '' }));
    }
  };

  const handleCardholderNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCardData((prev) => ({ ...prev, cardholderName: event.target.value }));
    if (fieldErrors.cardholderName) {
      setFieldErrors((prev) => ({ ...prev, cardholderName: '' }));
    }
  };

  const handleAddressChange = (address: Partial<BillingAddress>) => {
    setCardData((prev) => ({
      ...prev,
      billingAddress: {
        ...prev.billingAddress,
        ...address
      } as BillingAddress
    }));
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
      setIsButton(true);
        console.log('[CardFormContainer] Submit triggered from inside iframe (form submit event)');
    }
    // Prevent double submission
    if (isSubmitting) {
      console.warn('[CardFormContainer] Submission already in progress - ignoring');
      return;
    }

    setError(null);
    setFieldErrors({});

    // Run brand exclusion check and standard validation together so all errors show at once.
    // Detect brand directly from the current card number value to avoid stale cardType state.
    const currentCardType = detectCardType(cardData.cardNumber || '');
    const brandError = getBrandExclusionError(currentCardType, orderContext.excludeCards || []);
    const validation = validateCardData(cardData);

    const mergedErrors = { ...validation.errors };
    if (brandError) {
      mergedErrors.cardNumber = brandError;
    }
    if (brandError !== null) {
      setBrandExclusionError(brandError);
    }

    if (brandError || !validation.isValid) {
      setHasAttemptedSubmit(true); // Enable real-time validation after first submit attempt
      setFieldErrors(mergedErrors);
      setError('Please correct the errors below');
      const cleanedErrors = cleanValidationErrors(mergedErrors);
      // Send cleaned errors to parent
      postMessageService.send('CARD_FORM_VALIDATION_ERROR', { errors: cleanedErrors });
      return;
    }

    setIsSubmitting(true);

    try {
      // Call API to encrypt card data
      const response = await apiService.encryptCard(
          cardData as CardData,
          orderContext
      );
     
      console.log('[CardFormContainer] Encryption successful, correlationId:', response.correlationId);
      // Hide the submit button on success
      setSubmitButtonConfig(prev => ({ ...prev, show: false }));
      // Send success message to parent — pass API response through unchanged
      postMessageService.sendSuccess({
        orderContext: response.orderContext,
        encryptedPayload: response.encryptedPayload,
        keyVersion: response.keyVersion,
        last4Digits: response.last4Digits,
        cardType: response.cardType,
        correlationId: response.correlationId,
      });

      // Show confirmation box instead of the form
      setSuccessData({
        cardType: response.cardType,
        last4Digits: response.last4Digits,
        verificationDate: formatTimestampToDate(response.encryptedPayload.timestamp),
      });

      // Reset form fields and validation state
      setCardData({
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: '',
        cardholderName: '',
        billingAddress: {
          address1: '',
          address2: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US',
        },
      });
      setCardType('unknown');
      setHasAttemptedSubmit(false);
    } catch (err: unknown) {
      // Handle case where err is a rejected Promise (from apiService.handleError returning Promise.reject)
      let errorObj = err;
      if (err && typeof err === 'object' && 'then' in err) {
        errorObj = await (err as Promise<unknown>).catch((e: unknown) => e);
      }
      const error = errorObj as { detail?: string; message?: string; status?: number; errors?: Record<string, string[]> };

      // Build a human-readable message: prefer API detail, then flatten field-level
      // validation errors so the user sees exact messages instead of a generic fallback.
      let errorMessage = error.detail || error.message || '';
      if (error.errors && typeof error.errors === 'object') {
        const fieldMessages = cleanValidationErrors(error.errors);
        if (fieldMessages.length > 0) {
          errorMessage = fieldMessages.join('\n');
        }
      }
      if (!errorMessage) {
        errorMessage = 'An error occurred while processing your card';
      }
      console.log('[CardFormContainer] Encryption unsuccessful, status:', error.status);

      // Enable real-time validation for user to fix errors
      setHasAttemptedSubmit(true);
      
      // Set error message for display
      setError(errorMessage);

      // Send error message to parent — shape depends on status code
      if (error.status === 0) {
        postMessageService.sendError('Something went wrong, please try again later.', 'NETWORK_ERROR');
      } else if (error.status === 400) {
        // Normalize backend keys to camelCase frontend form field names.
        // FluentValidation with SetValidator emits dotted paths for nested validators,
        // e.g. "BillingAddress.PostalCode", "OrderContext.VendorId".
        const backendToCamelCase: Record<string, string> = {
          // Top-level fields
          CardNumber: 'cardNumber', ExpiryMonth: 'expiryMonth', ExpiryYear: 'expiryYear',
          CVV: 'cvv', Cvv: 'cvv', CardholderName: 'cardholderName',
          // Billing address nested paths (BillingAddressValidator via SetValidator)
          'BillingAddress.Address1': 'address1', 'BillingAddress.Address2': 'address2',
          'BillingAddress.City': 'city',         'BillingAddress.State': 'state',
          'BillingAddress.PostalCode': 'postalCode', 'BillingAddress.Country': 'country',
          // Order context nested paths (OrderContextValidator via SetValidator)
          'OrderContext.VendorId': 'vendorId',
        };
        const validationErrorsObj = error.errors
          ? Object.fromEntries(
              Object.entries(error.errors).map(([field, msgs]) => [
                backendToCamelCase[field] ?? (field.charAt(0).toLowerCase() + field.slice(1)),
                Array.isArray(msgs) ? msgs.join('; ') : String(msgs),
              ])
            )
          : undefined;
        let validationErrors: string[] | undefined = undefined;

        if (validationErrorsObj) {
          apiFieldErrorsRef.current = true; // Guard against useEffect overwriting inline errors
          // For the UI: collapse expiryMonth/expiryYear into a single expiryDate field.
          // Semantic backend messages are mapped to user-friendly equivalents;
          // all other cases (required, invalid format, both null) fall back to generic.
          const uiErrors = { ...validationErrorsObj };
          if (uiErrors.expiryMonth || uiErrors.expiryYear) {
            const expiryError = uiErrors.expiryMonth || uiErrors.expiryYear;
            delete uiErrors.expiryMonth;
            delete uiErrors.expiryYear;
            uiErrors.expiryDate = mapExpiryMessage(expiryError!);
          }
          setFieldErrors(uiErrors);
          // Send the original separate backend errors to parent unchanged
          const cleanedErrors = cleanValidationErrors(validationErrorsObj);
          validationErrors = cleanedErrors.length > 0 ? cleanedErrors : undefined;
        }
        postMessageService.sendError('One or more validation errors occurred.', 'VALIDATION_ERROR', validationErrors);
      } else if (error.status === 401) {
        postMessageService.sendError('Authentication failed. Please re-authenticate and try again.', 'AUTH_ERROR');
      } else {
        postMessageService.sendError('An error occurred during encryption.', 'ENCRYPTION_ERROR');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update ref with latest handleSubmit on every render
  handleSubmitRef.current = handleSubmit;

   if (successData && isButton) {
    return (
      <Container maxWidth={false} disableGutters className="desktop-fields-wrapper card-form-container">
        <Box sx={{ textAlign: 'left', mt: -3, margin: 2 }}>
          <Typography variant="h6" className='success-info' sx={{ fontSize: '14px' }}>
           Payment information captured successfully.
          </Typography>

          <Box className="success-field-row">
            <span className="success-field-label">Card Type</span>
            <Typography sx={{ fontSize: '14px' }}>
              {formatCardTypeLabel(successData.cardType)}
            </Typography>
          </Box>

          <Box className="success-field-row">
            <span className="success-field-label">Card Ending In</span>
            <Typography sx={{ fontSize: '14px' }}>
              {successData.last4Digits}
            </Typography>
          </Box>

          <Box className="success-field-row">
            <span className="success-field-label">Verification Date</span>
            <Typography sx={{ fontSize: '14px' }}>
              {successData.verificationDate}
            </Typography>
          </Box>
           <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1.5, mt: 1.5, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>
          </Box>
        </Box>
      </Container>
    );
  }

  const visibleBrands = ALL_SUPPORTED_BRANDS.filter(
    brand => !getBrandExclusionError(brand.toLowerCase() as CardType, orderContext.excludeCards ?? [])
  );

  return (
      <Container maxWidth={false} disableGutters className="desktop-fields-wrapper card-form-container">
          <Box className="card-form-header">
            <Typography className="card-form-header-title">Enter Payment Information</Typography>
            <Typography className="card-form-header-subtitle">Card types supported for secure detail capture.</Typography>
            <Box className="card-logos">
              {visibleBrands.map(brand => (
                <span key={brand} className={`card-icon ${brand.toLowerCase()}`} aria-label={brand} role="img" />
              ))}
            </Box>
          </Box>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <div className="field-container align-items-center">
                  <div className="label">
                    <label className="p-2" data-testid="CardholderName" htmlFor="cardholderName">
                      Name on Card
                    </label>
                    <span className="text-danger" data-testid="CardholderName-Astrict-icon">
                    *
                  </span>
                  </div>
                  <TextField
                      id="cardholderName"
                      className="textfield"
                      fullWidth
                      // label="Cardholder Name"
                      value={cardData.cardholderName || ''}
                      onChange={handleCardholderNameChange}
                      error={!!fieldErrors.cardholderName}
                      helperText={fieldErrors.cardholderName || ' '}
                      disabled={isSubmitting}
                      autoComplete="cc-name"
                      placeholder="John Doe"
                      required
                      autoFocus
                      inputProps={{
                        'aria-label': 'Cardholder name',
                        'aria-required': 'true',
                      }}
                  />
                </div>
              </Grid>

              <Grid item xs={12} sx={{ mt: 2, pt: '0 !important' }}>
                <CardNumberInput
                    value={cardData.cardNumber || ''}
                    onChange={handleCardNumberChange}
                    error={fieldErrors.cardNumber || brandExclusionError || undefined}
                    disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={6} sx={{ pt: '0 !important' }}>
                <ExpiryDateInput
                    month={cardData.expiryMonth || ""}
                    year={cardData.expiryYear || ""}
                    onChange={handleExpiryDateChange}
                    error={fieldErrors.expiryDate || undefined}
                    disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={6} sx={{ pt: '0 !important' }}>
                <CVVInput
                    value={cardData.cvv || ""}
                    onChange={handleCvvChange}
                    error={fieldErrors.cvv}
                    disabled={isSubmitting}
                    cardType={cardType}
                />
              </Grid>

              <Grid item xs={12}>
                <BillingAddressForm
                    address={cardData.billingAddress || {}}
                    onChange={handleAddressChange}
                    errors={fieldErrors}
                    disabled={isSubmitting}
                />
              </Grid>

              {/* Configurable submit button */}
              {(() => {
                console.log('[CardFormContainer] RENDER - submitButtonConfig.show:', submitButtonConfig.show);
                return null;
              })()}
            
              {submitButtonConfig.show && (
                  <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth={isMobile}
                        disabled={isSubmitting || submitButtonConfig.disabled}
                        aria-label={submitButtonConfig.label.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}
                        aria-busy={isSubmitting}
                        aria-describedby={error ? 'form-error' : undefined}
                        sx={{
                          mt: 2,
                          mb: '4px',
                          minHeight: '44px',
                          textTransform: 'none',
                          backgroundColor: submitButtonConfig.color,
                          color: submitButtonConfig.foregroundColor,
                          '&:hover': {
                            backgroundColor: darkenColor(submitButtonConfig.color, 0.1),
                            color: submitButtonConfig.foregroundColor,
                          },
                          '&:disabled': {
                            backgroundColor: '#e0e0e0',
                            color: '#9e9e9e',
                          },
                          ...(!isMobile && {
                            minWidth: '200px',
                          }),
                        }}
                    >
                      {isSubmitting ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1, color: '#ffffff' }} />
                            Processing...
                          </>
                      ) : (
                          submitButtonConfig.label.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
                      )}
                    </Button>
                  </Grid>
              )}

              {/* Loading indicator when submit button is hidden */}
              {!submitButtonConfig.show && isSubmitting && (
                  <Grid item xs={12} sx={{ mt: 2, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Processing your payment securely...
                    </Typography>
                  </Grid>
              )}
            </Grid>           
          </Box>
      </Container>
  );
};

export default CardFormContainer;
